class VersionService {
  constructor() {
    this.versionInfo = null
    this.manifestVersion = null
    // Safely get environment, handling both browser and Node.js contexts
    this.environment = this.getEnvironment()
  }

  /**
   * Safely get environment from various sources
   */
  getEnvironment() {
    // Try import.meta.env first (Vite/browser context)
    try {
      if (process && process.env && process.env.VITE_ENVIRONMENT) {
        return process.env.VITE_ENVIRONMENT
      }
    } catch (error) {
      // import.meta not available in this context
    }
    
    // Try process.env (Node.js context)
    if (typeof process !== 'undefined' && process.env) {
      return process.env.VITE_ENVIRONMENT || process.env.VITE_APP_ENV || 'UAT'
    }
    
    // Fallback
    return 'development'
  }

  /**
   * Load version from manifest.xml in dist folder
   */
  async loadManifestVersion() {
    if (this.manifestVersion) {
      return this.manifestVersion
    }

    try {
      // Fetch the manifest.xml from dist folder with cache-busting
      const timestamp = Date.now()
      const manifestUrl = `./manifest.xml?v=${timestamp}&nocache=1`
      const response = await fetch(manifestUrl)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch manifest: ${response.status}`)
      }
      
      const manifestXml = await response.text()
      
      // Parse the XML to extract version
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(manifestXml, 'text/xml')
      const versionElement = xmlDoc.getElementsByTagName('Version')[0]
      
      if (versionElement && versionElement.textContent) {
        this.manifestVersion = versionElement.textContent.trim()
      } else {
        throw new Error('Version element not found in manifest.xml')
      }
      
      return this.manifestVersion
    } catch (error) {
      console.warn('⚠️ Could not load version from manifest.xml:', error.message)
      
      // Fallback to package.json version
      try {
        const packageJson = await import('../../package.json')
        this.manifestVersion = packageJson.default.version || '1.0.0'
        return this.manifestVersion
      } catch (fallbackError) {
        this.manifestVersion = '1.0.0'
        return this.manifestVersion
      }
    }
  }

  /**
   * Load full version info
   */
  async loadVersionInfo() {
    if (this.versionInfo) {
      return this.versionInfo
    }

    const manifestVersion = await this.loadManifestVersion()
    const buildTime = new Date().toISOString()
    
    this.versionInfo = {
      manifestVersion: manifestVersion,
      buildTime: buildTime,
      environment: this.environment,
      cacheTimestamp: Date.now()
    }
    
    return this.versionInfo
  }

  /**
   * Get the display version for the UI
   * Uses manifest version
   */
  async getDisplayVersion() {
    const manifestVersion = await this.loadManifestVersion()
    return manifestVersion
  }

  /**
   * Get full version information
   */
  async getVersionInfo() {
    return await this.loadVersionInfo()
  }

  /**
   * Get environment-specific version label
   */
  async getVersionLabel() {
    const version = await this.getDisplayVersion()
    const env = this.environment
    
    if (env === 'local' || env === 'dev') {
      return `v${version}`
    } else if (env === 'uat') {
      return `v${version}`
    } else if (env === 'production') {
      return `v${version}`
    } else {
      return `v${version}`
    }
  }

  /**
   * Get add-in display name
   */
  async getAddInName() {
    return 'TOPS2 Actions'
  }

  /**
   * Get build time
   */
  async getBuildTime() {
    const info = await this.loadVersionInfo()
    return info.buildTime
  }

  /**
   * Force refresh version from manifest.xml (clears cache)
   * Useful after deployments to get the latest version immediately
   */
  async refreshVersion() {
    this.manifestVersion = null
    this.versionInfo = null
    
    const version = await this.loadManifestVersion()
    return version
  }

  /**
   * Get cache-busting URL for any resource
   */
  getCacheBustingUrl(baseUrl) {
    const timestamp = Date.now()
    const separator = baseUrl.includes('?') ? '&' : '?'
    return `${baseUrl}${separator}v=${timestamp}&nocache=1`
  }
}

export default new VersionService()
